import React, { useContext, useEffect } from 'react';
import GiftItemBanner from '@components/GiftItemBanner';
import {
  Banners,
  CreditCardStatusBanner,
} from '@components/Layout/Header/Banners';
import { LayoutContext } from '@context/LayoutContext';
import NavigationTop from '@components/Layout/Header/components/NavigationTop';
import Link from 'next/link';
import { Logo } from '@components/Logo';
import GlobalSearch from '@components/Layout/Header/GlobalSearch';
import Menu from '@components/Layout/Header/components/Menu';
import { NavigationCollections } from '@components/Layout/Header/components/NavigationCollections';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { EXPIRED_CARD_BANNER } from '@monofront/business/constants';
import { useRef } from 'react';
import { useMediaQuery } from '@monofront/business/hooks';
import { useCallback } from 'react';
import { useStateRef } from '@monofront/business/utils';
import { useGlobalSearchContext } from '@context/GlobalSearchContext';
import { useLayoutRefs } from '@context/layoutRefs';
import { usePathname } from 'next/navigation';
import { useScrollDirectionChange } from './useScrollDirectionChange';

const useMobileAnimations = ({
  isSmallHeader,
  searchRef,
  headerContainerRef,
  headerAddonsRef,
  headerCartRef,
  headerSearchAutocompleteRef,
}: {
  isSmallHeader: React.MutableRefObject<boolean>;
  searchRef: React.MutableRefObject<HTMLDivElement | null>;
  headerContainerRef: React.RefObject<HTMLDivElement | null>;
  headerAddonsRef: React.RefObject<HTMLDivElement | null>;
  headerCartRef: React.RefObject<HTMLDivElement | null>;
  headerSearchAutocompleteRef: React.RefObject<HTMLDivElement | null>;
}) => {
  const { isGlobalSearchResultsPanelOpen } = useGlobalSearchContext();
  const isMobile = useMediaQuery(800);
  const isMobileRef = useStateRef(isMobile);
  const pathname = usePathname();
  const isCartLocation = pathname.includes('cart');

  useEffect(() => {
    // Reset styling when going from mobile to desktop
    if (!isMobile && searchRef.current && headerContainerRef.current) {
      searchRef.current.style.transform = 'initial';
      searchRef.current.style.width = 'initial';
      headerContainerRef.current.style.height = 'initial';
      isSmallHeader.current = false;
    }
  }, [isMobile]);

  const handleScrollDownAction = useCallback(() => {
    if (
      isMobileRef.current &&
      searchRef.current &&
      headerContainerRef.current
    ) {
      const size =
        (headerCartRef.current?.getBoundingClientRect().width || 30) + 55;

      searchRef.current.style.transform = 'translateY(-42px) translateX(40px)';
      searchRef.current.style.width = `calc(100% - ${size}px)`;
      headerContainerRef.current.style.height =
        'var(--mobile-header-minheight)';
      isSmallHeader.current = true;

      if (headerAddonsRef.current && !isMobileRef.current && !isCartLocation) {
        headerAddonsRef.current.style.transform = 'translateY(-46px)';
      }
    }
  }, []);

  const handleScrollUpAction = useCallback(() => {
    if (
      isMobileRef.current &&
      searchRef.current &&
      headerContainerRef.current
    ) {
      searchRef.current.style.transform = '';
      searchRef.current.style.width = '100%';
      headerContainerRef.current.style.height = 'initial';
      isSmallHeader.current = false;

      if (headerAddonsRef.current) {
        headerAddonsRef.current.style.transform = 'translateY(0px)';
      }
    }
  }, []);

  const handleInputFocus = () => {
    if (
      isMobileRef.current &&
      searchRef.current &&
      headerContainerRef.current &&
      headerSearchAutocompleteRef.current
    ) {
      searchRef.current.style.transitionDuration = 'unset';
      searchRef.current.style.width = '100%';
      searchRef.current.style.transform = 'initial';
      headerContainerRef.current.style.height = 'initial';
    }
  };

  const handleInputBlur = () => {
    if (
      isMobileRef.current &&
      searchRef.current &&
      headerContainerRef.current &&
      headerSearchAutocompleteRef.current
    ) {
      if (isSmallHeader.current) {
        handleScrollDownAction();
      } else {
        handleScrollUpAction();
      }

      requestAnimationFrame(() => {
        if (searchRef.current) {
          searchRef.current.style.transitionDuration = '300ms';
        }
      });
    }
  };

  useScrollDirectionChange(({ direction }) => {
    if (isGlobalSearchResultsPanelOpen) {
      return;
    }

    if (direction === 'up') {
      handleScrollUpAction();

      return;
    }

    if (direction === 'down') {
      handleScrollDownAction();

      return;
    }
  });

  return { handleInputFocus, handleInputBlur };
};

const Header: React.FC<{
  isExpiredCardBannerEnabled: boolean;
}> = React.memo(({ isExpiredCardBannerEnabled }) => {
  const { headerTopBanners } = useContext(LayoutContext);
  const {
    headerContainerRef,
    headerAddonsRef,
    headerCartRef,
    headerSearchAutocompleteRef,
  } = useLayoutRefs();

  const isSmallHeader = useRef(false);
  const searchRef = useRef<HTMLDivElement>(null);

  const pathname = usePathname();
  const isCartLocation = pathname.includes('cart');

  const { handleInputFocus, handleInputBlur } = useMobileAnimations({
    isSmallHeader,
    searchRef,
    headerContainerRef,
    headerAddonsRef,
    headerCartRef,
    headerSearchAutocompleteRef,
  });

  return (
    <>
      <header
        id="default-header"
        className="main-header tw-sticky tw-inset-x-0 tw-top-0 tw-z-sticky tw-bg-white tw-shadow-[0_2px_16px_0_rgba(0,0,0,.06)]"
      >
        <GiftItemBanner />
        <Banners prismicContent={headerTopBanners} />
        <NavigationTop />
        <div
          ref={headerContainerRef}
          className={`tw-mx-auto tw-grid tw-max-w-[1440px] tw-items-center tw-gap-x-4 tw-px-4 ${
            isCartLocation
              ? '[&>*:first-child]:tw-block [&>*]:tw-hidden [&>*]:md:tw-block'
              : ''
          }`}
          style={{
            gridTemplateRows:
              '[top] max-content [bottom] max-content [addons] max-content',
            gridTemplateColumns:
              '[left] max-content [left-bis] max-content [middle] 1fr [right] max-content',
          }}
        >
          {/** Render first so it is below everything, while actually being the last row */}
          <div
            ref={headerAddonsRef}
            id="header-addons"
            className="tw-col-[left_/_-1] tw-row-[addons] -tw-mx-4 tw-transition-all tw-duration-300"
          />

          <div className="logo tw-col-[left-bis] tw-row-[top] tw-w-[58px] desktop:tw-col-[left] desktop:tw-my-2 desktop:tw-w-[125px]">
            <Link href="/" data-testid="BodyNonAuthenticated-go-to-home">
              <Logo />
            </Link>
          </div>

          <div className="tw-col-[right] tw-row-[top] tw-flex tw-items-center">
            <Menu />
          </div>

          <div
            className={`tw-col-[left] tw-row-[top] tw-my-2 tw-overflow-x-auto desktop:tw-col-[left_/_-1] desktop:tw-row-[bottom] desktop:tw-my-0`}
          >
            <NavigationCollections />
          </div>

          <div
            ref={searchRef}
            className="tw-col-[left_/_-1] tw-row-[bottom] tw-flex tw-items-center tw-justify-center tw-transition-all tw-duration-300 desktop:tw-col-[left-bis_/_right] desktop:tw-row-[top]"
          >
            <GlobalSearch
              handleInputFocus={handleInputFocus}
              handleInputBlur={handleInputBlur}
            />
          </div>
        </div>
      </header>

      {isExpiredCardBannerEnabled && <CreditCardStatusBanner />}
    </>
  );
});

const DefaultHeader = () => {
  const isExpiredCardBannerEnabled = useFeatureIsOn(EXPIRED_CARD_BANNER);

  return <Header isExpiredCardBannerEnabled={isExpiredCardBannerEnabled} />;
};

export default DefaultHeader;
